import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    inject,
} from '@angular/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { DATE_FILTER_CONFIG } from '@wdx/shared/components/ag-grid';
import { WdxDropdownModule } from '@wdx/shared/components/wdx-dropdown';
import { WdxIconModule } from '@wdx/shared/components/wdx-icon';
import { WdxIconContainerModule } from '@wdx/shared/components/wdx-icon-container';
import { WdxInfoCardModule } from '@wdx/shared/components/wdx-info-card';
import {
    WdxRadioButtonGroupConfig,
    WdxRadioButtonGroupModule,
} from '@wdx/shared/components/wdx-radio-button-group';
import { WdxSpinnerModule } from '@wdx/shared/components/wdx-spinner';
import { WdxTabsModule } from '@wdx/shared/components/wdx-tabs';
import {
    FriendlyProblemDetails,
    IMeLocale,
    PerformancePeriod,
    TranslateTokenisedStringPipe,
    TranslationsService,
    WdxDestroyClass,
    WdxNumberService,
} from '@wdx/shared/utils';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import {
    ChartData,
    PerformanceComponentEvent,
    PerformanceSummaryCard,
    PerformanceTabConfig,
    PortfolioPerformanceChartTypes,
} from '../models';
import {
    PortfolioLoadingStates,
    PortfolioNotLoadedComponent,
} from '../portfolio-not-loaded/portfolio-not-loaded.component';
import { PortfolioPerformanceComponentService } from './portfolio-performance.component.service';

export const CHART_TABS: PerformanceTabConfig = {
    value: { label: 'PORTOFOLIO_INSIGHTS_VALUE', hcChartType: 'spline' },
    contributionsWithdrawals: {
        label: 'PORTOFOLIO_INSIGHTS_CONTRIBUTIONS_WITHDRAWALS',
        hcChartType: 'spline',
    },
    performance: {
        label: 'PORTOFOLIO_INSIGHTS_RATE_RETURN',
        hcChartType: 'spline',
    },
};

@Component({
    selector: 'wdx-portfolio-performance',
    imports: [
        CommonModule,
        WdxIconModule,
        WdxIconContainerModule,
        WdxDropdownModule,
        NgbDropdownModule,
        HighchartsChartModule,
        WdxSpinnerModule,
        WdxInfoCardModule,
        WdxRadioButtonGroupModule,
        WdxTabsModule,
        WdxInfoCardModule,
        PortfolioNotLoadedComponent,
        TranslateTokenisedStringPipe,
    ],
    providers: [PortfolioPerformanceComponentService],
    templateUrl: './portfolio-performance.component.html',
    styleUrls: ['./portfolio-performance.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortfolioPerformanceComponent
    extends WdxDestroyClass
    implements OnChanges
{
    private componentService = inject(PortfolioPerformanceComponentService);
    private localeService = inject(IMeLocale);
    private numberService = inject(WdxNumberService);
    private translationsService = inject(TranslationsService);

    Highcharts = Highcharts;

    @Input() selectedDateRange: PerformancePeriod = PerformancePeriod.OneYear;
    @Input() summaryCards?: PerformanceSummaryCard[] = [];
    public formattedSummaryCards: PerformanceSummaryCard[] = [];
    @Input() dataTypeLabel = 'Performance';
    @Input() dataConnectionError?: FriendlyProblemDetails = undefined;

    private _chartTabs = {
        selected: 'portfolioValue' as PortfolioPerformanceChartTypes,
        tabs: [
            'value',
            'contributionsWithdrawals',
            'performance',
        ] as PortfolioPerformanceChartTypes[],
        config: CHART_TABS,
    };
    @Input() set chartTabs(
        value:
            | {
                  selected?: PortfolioPerformanceChartTypes;
                  tabs?: PortfolioPerformanceChartTypes[];
                  config?: PerformanceTabConfig;
              }
            | undefined,
    ) {
        if (!value) {
            return;
        }
        const config = value.config || this._chartTabs.config;
        const selected = value.selected || this._chartTabs.selected;
        const tabs = value.tabs || this._chartTabs.tabs;
        this._chartTabs = {
            config,
            selected,
            tabs,
        };
        this.emitSelectionChange();
    }
    get chartTabs() {
        return this._chartTabs;
    }

    public chartOptions?: Highcharts.Options;
    @Input() set chartData(value: ChartData | undefined) {
        if (!value) {
            this.chartOptions = undefined;
            this.status = 'loading';
            return;
        }
        const chartType =
            this.chartTabs?.selected === 'contributionsWithdrawals'
                ? 'column'
                : 'spline';
        this.status = 'loaded';
        this.chartOptions = {
            chart: { type: chartType },
            lang: {
                noData: this.translationsService.translateTokenisedString(
                    'PORTOFOLIO_INSIGHTS_NO_DATA_TO_DISPLAY',
                ),
            },
            title: { text: undefined },
            subtitle: {},
            xAxis: this.componentService.createHighChartXAxis(value.dates),
            yAxis: { title: { text: undefined } },
            tooltip: {
                shared: true,
                headerFormat:
                    '<span style="font-size: 10px">{point.key}</span><br/>',
                pointFormat:
                    '<span style="color:{point.color}">●</span> {series.name}: <b>{point.custom.tooltip.value}</b><br/>',
            },
            plotOptions: {
                spline: {
                    marker: { radius: 4, lineColor: '#3070a2', lineWidth: 1 },
                },
            },
            exporting: {
                enabled: true,
            },
            series: this.componentService.convertChartSeriesToHighchartFormat(
                value.seriesValues,
                value.currency?.isoCode,
                chartType,
            ),
        };
    }

    @Output() chartSelection = new EventEmitter<PerformanceComponentEvent>();

    public dateFiltersConfig: WdxRadioButtonGroupConfig[] = DATE_FILTER_CONFIG;
    public status: PortfolioLoadingStates = 'loading';

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['summaryCards']) {
            this.formattedSummaryCards =
                this.summaryCards?.map((cardData) => ({
                    ...cardData,
                    valueLabel: this.formatSummaryCardValue(
                        cardData.value,
                        cardData.currencyCode,
                    ),
                })) || [];
        }
    }

    public dateFilterSelection(selected: string) {
        this.selectedDateRange = selected as PerformancePeriod;
        this.emitSelectionChange();
    }

    public selectTab(tab: PortfolioPerformanceChartTypes) {
        if (!this.chartTabs) {
            return;
        }
        this.chartTabs.selected = tab;
        this.emitSelectionChange();
    }

    private formatSummaryCardValue(
        value: number,
        currencyCode?: string | null,
    ): string {
        const locale = this.localeService.getMeLocale();

        return (
            this.numberService.formatLocalisedCurrency(value, {
                currencyCode: currencyCode || locale?.currency?.isoCode || '',
                locale: locale?.code,
                groupSeparator: locale?.numberFormat?.numberGroupSeparator,
                decimalSeparator: locale?.numberFormat?.numberDecimalSeparator,
            }) || ''
        );
    }

    private emitSelectionChange() {
        const chartKey = this.chartTabs?.selected;
        if (!chartKey) {
            return;
        }
        this.chartSelection.emit({
            chartKey,
            dateRange: this.selectedDateRange,
        });
    }
}
